import {
  BMC_LABEL, DNA_SOURCES, HORIZON_KEYS, HORIZON_META, INNOVATION_CATEGORIES, INNOVATION_META, MACRO_DIMENSIONS, MACRO_DIMENSION_META,
  MACRO_QUADRANTS, MACRO_QUADRANT_META, PRIORIDADES, PRIORITY_LABEL, STRATEGY_TOOLS,
  composeTriade, diffMetrics, evidenceWord, gapsMetrics, horizonMetrics, innovationMetrics, isOverdue, localToday, macroCell, macroMetrics, sortActions, w5h2Metrics,
  type BmcListBlock, type DnaOverviewDto, type Horizontes, type StrategyToolKey, type W5h2Action,
} from '@ads/shared';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { ViewSkeleton } from '../components/Skeleton';
import { SmallDial } from '../components/SmallDial';
import { brl, dateBR, dateTime } from '../lib/format';
import { useConsolidate, useDnaOverview } from '../lib/queries';
import { useWidth } from '../lib/useWidth';
import { HorizonChart } from './Horizontes';

/* ─────────────────────────────────────────────────────────────────────
   DNA Estratégico — o Strategy inteiro numa tela.
   Não escreve nada novo: organiza, ao vivo, o que as dez ferramentas e
   as leituras já têm. Um capítulo por pergunta — quem somos, onde
   estamos, o contexto, para onde vamos, como chegamos lá — e a versão
   que o Studio usa, consolidada sozinha quando o Strategy muda.
   ───────────────────────────────────────────────────────────────────── */

type O = DnaOverviewDto;
const CHAPTERS = [
  { id: 'identidade', n: '01', title: 'Identidade', lede: 'Quem a organização é, no que acredita e o que a torna difícil de copiar.' },
  { id: 'diagnostico', n: '02', title: 'Diagnóstico', lede: 'Onde a organização está hoje: a maturidade, o que falta e como o negócio funciona.' },
  { id: 'contexto', n: '03', title: 'Contexto', lede: 'O que acontece em volta — e o que isso pede da estratégia.' },
  { id: 'inovacao', n: '04', title: 'Inovação', lede: 'Para onde vamos: os tipos de inovação e as iniciativas em três horizontes.' },
  { id: 'execucao', n: '05', title: 'Execução', lede: 'Como chegamos lá: as ações, quem faz, quando e quanto custa.' },
] as const;
type ChapterId = (typeof CHAPTERS)[number]['id'];

const n1 = (x: number) => x.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const toolName = (k: StrategyToolKey) => STRATEGY_TOOLS.find(t => t.key === k)!.name;
const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((s, x) => s + x, 0) / xs.length) : null);

export function Dna() {
  useViewMeta({ nav: 'dna', crumb: ['DNA', 'A estratégia inteira'], j: .64 });
  const nav = useNavigate();
  const q = useDnaOverview();
  const consolidate = useConsolidate();
  const o = q.data;
  const root = useRef<HTMLElement>(null);
  const [active, setActive] = useState<ChapterId>('identidade');

  /* a versão do Studio ficou para trás (o Strategy mudou): consolida, uma vez por mudança */
  const tried = useRef<string | null>(null);
  useEffect(() => {
    if (!o || o.snapshot?.fresh || consolidate.isPending) return;
    const mark = `${o.updatedAt}|${o.live.score}`;
    if (tried.current === mark) return;
    tried.current = mark;
    consolidate.mutate();
  }, [o, consolidate]);

  /* os blocos aparecem conforme entram na tela; o capítulo ativo acompanha a rolagem */
  useEffect(() => {
    const el = root.current;
    if (!el || !o) return;
    const reveal = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); reveal.unobserve(e.target); } }), { rootMargin: '0px 0px -8% 0px' });
    el.querySelectorAll('.dx-rv:not(.in)').forEach(x => reveal.observe(x));
    const spy = new IntersectionObserver(es => {
      const vis = es.filter(e => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (vis) setActive(vis.target.id as ChapterId);
    }, { rootMargin: '-30% 0px -60% 0px' });
    el.querySelectorAll('.dx-ch').forEach(x => spy.observe(x));
    return () => { reveal.disconnect(); spy.disconnect(); };
  }, [o]);

  const go = (id: ChapterId) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section className="view on dx" data-v="dna" ref={root}>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>DNA</span><b>A estratégia inteira</b></div>
          <h1>DNA Estratégico</h1>
          <p className="lede">Tudo o que o Strategy construiu, numa tela — e a camada de contexto que o Studio usa em cada entrega.</p>
        </div>
        {o && (
          <div className="tools">
            <span className={`dx-ver${o.snapshot?.fresh ? ' ok' : ''}`} title={o.snapshot ? `Consolidada em ${dateTime(o.snapshot.createdAt)}` : undefined}>
              {consolidate.isPending || !o.snapshot?.fresh ? <><span className="spin" />Atualizando a versão do Studio</> : <><Ic.Check />Versão {o.snapshot.version} · em dia com o Strategy</>}
            </span>
          </div>
        )}
      </div>

      {!o ? (q.isError ? <p className="gd-empty">Não foi possível carregar o DNA agora.</p> : <ViewSkeleton cards={6} />) : (
        <>
          <Hero o={o} onTool={k => nav(`/strategy/${k}`)} />

          <nav className="dx-nav" aria-label="Capítulos">
            {CHAPTERS.map(c => (
              <button key={c.id} type="button" className={active === c.id ? 'on' : ''} aria-current={active === c.id ? 'true' : undefined} onClick={() => go(c.id)}>
                <span className="n">{c.n}</span>{c.title}
              </button>
            ))}
          </nav>

          <Chapter id="identidade" links={[['triade', 'Tríade'], ['valores', 'Valores'], ['diferenciais', 'Diferenciais']]}>
            <Identidade o={o} />
          </Chapter>
          <Chapter id="diagnostico" links={[['gaps', 'Mapa de Gaps'], ['bmc', 'Business Model Canvas']]}>
            <Diagnostico o={o} />
          </Chapter>
          <Chapter id="contexto" links={[['macro', 'Macro Cenário']]}>
            <Contexto o={o} />
          </Chapter>
          <Chapter id="inovacao" links={[['inovacao', '10 Tipos'], ['horizontes', '3 Horizontes']]}>
            <Inovacao o={o} />
          </Chapter>
          <Chapter id="execucao" links={[['w5h2', 'Plano de Ação']]}>
            <Execucao o={o} />
          </Chapter>

          <Studio o={o} />
        </>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => nav('/strategy')}>Abrir o Strategy</button>
        <button className="btn-primary" type="button" onClick={() => nav('/studio')}>Abrir o Studio<Ic.ArrowRight /></button>
      </Actions>
    </section>
  );
}

/* ── moldura ──────────────────────────────────────────────────────── */

function Chapter({ id, links, children }: { id: ChapterId; links: Array<[StrategyToolKey, string]>; children: ReactNode }) {
  const nav = useNavigate();
  const c = CHAPTERS.find(x => x.id === id)!;
  return (
    <section className="dx-ch" id={id} aria-labelledby={`${id}-t`}>
      <header className="dx-ch-h dx-rv">
        <span className="dx-ch-n" aria-hidden="true">{c.n}</span>
        <div><h2 id={`${id}-t`}>{c.title}</h2><p>{c.lede}</p></div>
        <div className="dx-ch-l">{links.map(([k, label]) => <button key={k} type="button" onClick={() => nav(`/strategy/${k}`)}>{label}<Ic.ArrowRight /></button>)}</div>
      </header>
      <div className="dx-grid">{children}</div>
    </section>
  );
}

function Block({ span = 12, title, icon, aside, tool, empty, children, className = '' }: {
  span?: number; title: string; icon?: ReactNode; aside?: ReactNode; tool?: StrategyToolKey; empty?: string | false; children?: ReactNode; className?: string;
}) {
  const nav = useNavigate();
  return (
    <article className={`dx-b dx-rv s${span} ${className}`}>
      <header className="dx-b-h"><h3>{icon}{title}</h3>{aside}</header>
      {empty ? (
        <div className="dx-empty"><p>{empty}</p>{tool && <button type="button" className="addlink" onClick={() => nav(`/strategy/${tool}`)}>Abrir {toolName(tool)}<Ic.ArrowRight /></button>}</div>
      ) : children}
    </article>
  );
}

const Stat = ({ v, label, tone }: { v: ReactNode; label: string; tone?: string }) => <span className={`dx-stat${tone ? ` ${tone}` : ''}`}><b>{v}</b>{label}</span>;

/* ── abertura ─────────────────────────────────────────────────────── */

function Hero({ o, onTool }: { o: O; onTool: (k: StrategyToolKey) => void }) {
  const v = o.contents.visao;
  const r = o.readings;
  const ai = avg([r.visao?.alignment, r.bmc?.alinhamento, r.inovacao?.alinhamento, r.macro?.alinhamento, r.horizontes?.alinhamento].filter((x): x is number => x != null));
  const done = o.tools.filter(x => x.state === 'done').length;
  const delta = o.snapshot?.delta;
  return (
    <section className="dx-hero dx-rv" aria-label="Síntese">
      <div className="dx-hero-main">
        <p className="dx-org">{[o.org.name, o.org.segment, o.org.location, o.project.cycle && `ciclo ${o.project.cycle}`].filter(Boolean).join(' · ')}</p>
        <p className="dx-k">Propósito</p>
        {v?.purpose ? <blockquote className="dx-purpose">{v.purpose}</blockquote> : <p className="dx-purpose none">O propósito ainda não foi escrito.</p>}
        <div className="dx-hero-row">
          <div>
            <p className="dx-k">Visão{v?.horizonYear ? ` · até ${v.horizonYear}` : ''}</p>
            <p className="dx-vision">{v?.vision || '—'}</p>
          </div>
          <div>
            <p className="dx-k">Objetivo central</p>
            <p className="dx-goal">{o.goal.interpreted || o.goal.text || '—'}</p>
            {(o.goal.horizon || o.goal.focus) && <p className="dx-tags">{[o.goal.horizon, o.goal.focus].filter(Boolean).map(x => <span key={x}>{x}</span>)}</p>}
          </div>
        </div>
      </div>
      <aside className="dx-hero-side">
        <div className="dx-score">
          <SmallDial value={o.live.score} size={124} label={o.live.score} caption="de 100" />
          <div>
            <b>Score do posicionamento</b>
            <span>Completude das dez ferramentas e maturidade do diagnóstico.</span>
            <em className={delta == null ? '' : delta < 0 ? 'neg' : 'pos'}>{delta == null ? 'Primeira versão' : delta === 0 ? 'Igual à versão anterior' : `${delta > 0 ? '+' : ''}${delta} desde a versão anterior`}</em>
          </div>
        </div>
        <dl className="dx-meters">
          <div><dt title="Média das leituras da IA: visão, modelo de negócio, inovação, macro cenário e horizontes">Alinhamento ao propósito</dt><dd><Meter v={ai ?? 0} /><b>{ai != null ? `${ai}%` : '—'}</b></dd></div>
          <div><dt title="Quanto da maturidade desejada no Mapa de Gaps já foi alcançado">Caminho até as metas</dt><dd><Meter v={o.live.alignment} /><b>{o.live.alignment}%</b></dd></div>
        </dl>
        <div className="dx-tools">
          <p><span>Strategy</span><b>{done} de 10</b> completas</p>
          <ol>
            {STRATEGY_TOOLS.map(tk => {
              const s = o.tools.find(x => x.key === tk.key)!;
              return (
                <li key={tk.key}>
                  <button type="button" className={`st-${s.state}`} onClick={() => onTool(tk.key)} title={`${tk.no} · ${tk.name} — ${Math.round(s.progress * 100)}%`}
                    aria-label={`${tk.name}: ${Math.round(s.progress * 100)}%`} style={{ '--p': `${Math.round(s.progress * 100)}%` } as CSSProperties}>
                    <i /><span>{tk.no}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      </aside>
    </section>
  );
}

const Meter = ({ v }: { v: number }) => <span className="dx-meter"><i style={{ '--v': `${Math.max(0, Math.min(100, v))}%` } as CSSProperties} /></span>;

/* ── 01 · identidade ─────────────────────────────────────────────── */

function Identidade({ o }: { o: O }) {
  const t = o.contents.triade, vals = (o.contents.valores?.values ?? []).filter(v => v.name.trim());
  const d = o.contents.diferenciais;
  const m = d ? diffMetrics(d, o.readings.diferenciais) : null;
  const rows = m ? [...m.rows].filter(r => r.label.trim()).sort((a, b) => (b.ia?.sum ?? b.user.sum) - (a.ia?.sum ?? a.user.sum)) : [];
  const parts = t ? [['Acreditamos em', t.believe], ['Por meio de', t.through], ['Resultando em', t.result]] as const : [];
  return (
    <>
      <Block title="Tríade de Valor" icon={<Ic.Triad />} tool="triade" empty={!parts.some(([, x]) => x.trim()) && 'A tríade ainda não foi preenchida.'}>
        {t && <p className="dx-phrase">{t.synthesis.trim() || composeTriade(t)}</p>}
        <ol className="dx-triad">
          {parts.map(([k, x], i) => <li key={k}><span className="dx-k">{k}</span><p>{x || '—'}</p>{i < 2 && <Ic.ChevronRight />}</li>)}
        </ol>
      </Block>
      <Block span={7} title="Valores" icon={<Ic.Heart />} tool="valores" aside={vals.length ? <Stat v={vals.length} label="valores" /> : null} empty={!vals.length && 'Os valores ainda não foram escritos.'}>
        <ul className="dx-values">{vals.map(v => <li key={v.id ?? v.name}><b>{v.name}</b>{v.meaning && <span>{v.meaning}</span>}</li>)}</ul>
      </Block>
      <Block span={5} title="Diferenciais competitivos" icon={<Ic.Star />} tool="diferenciais"
        aside={m?.total.ia ? <Stat v={`${m.total.ia.pct}%`} label="força real (IA)" /> : m ? <Stat v={`${m.total.user.pct}%`} label="força declarada" /> : null}
        empty={!rows.length && 'Os diferenciais ainda não foram avaliados.'}>
        <ol className="dx-rank">
          {rows.slice(0, 5).map((r, i) => (
            <li key={r.id || i}>
              <span className="k">{i + 1}</span>
              <span className="nm">{r.label}</span>
              <span className="dx-dual" title={`Equipe ${r.user.sum}/15${r.ia ? ` · IA ${r.ia.sum}/15` : ''}`}>
                <i className="a" style={{ '--v': `${(r.user.sum / 15) * 100}%` } as CSSProperties} />
                {r.ia && <i className="b" style={{ '--v': `${(r.ia.sum / 15) * 100}%` } as CSSProperties} />}
              </span>
              <b>{r.ia?.sum ?? r.user.sum}</b>
              {r.ia?.leitura && <p className="why">{r.ia.leitura}</p>}
            </li>
          ))}
        </ol>
        {m?.rows.some(r => r.ia) && <p className="dx-legend"><span className="a" />equipe <span className="b" />leitura da IA · de 15</p>}
        {o.readings.diferenciais?.evidencias.leitura && (
          <div className="dx-sub"><p className="dx-k">Evidências · {evidenceWord(o.readings.diferenciais.evidencias.score)}</p><p className="dx-text dx-note">{o.readings.diferenciais.evidencias.leitura}</p></div>
        )}
      </Block>
    </>
  );
}

/* ── 02 · diagnóstico ────────────────────────────────────────────── */

function Diagnostico({ o }: { o: O }) {
  const g = o.contents.gaps;
  const gm = g ? gapsMetrics(g) : null;
  const real = new Map((o.readings.gaps?.maturidadeReal ?? []).map(x => [x.factor.toLowerCase(), x.value]));
  const factors = gm?.factors.filter(f => f.maturity > 0) ?? [];
  const rd = o.readings.gaps;
  return (
    <>
      <Block span={7} title="Mapa de Gaps" icon={<Ic.Bars />} tool="gaps"
        aside={gm ? <Stat v={`${gm.evidence.pct}%`} label="com evidência" /> : null} empty={!factors.length && 'O diagnóstico ainda não foi preenchido.'}>
        <ul className="dx-factors">
          {factors.map(f => {
            const r = real.get(f.name.toLowerCase()) ?? real.get(f.name.replace(/^Fatores?\s+/i, '').toLowerCase());
            return (
              <li key={f.key}>
                <span className="nm">{f.name.replace(/^Fatores?\s+/i, '')}</span>
                <span className="dx-scale" title={`Maturidade ${n1(f.maturity)} · meta ${n1(f.target)}${r != null ? ` · leitura da IA ${n1(r / 20)}` : ''}`}>
                  <i className="cur" style={{ '--v': `${(f.maturity / 5) * 100}%` } as CSSProperties} />
                  {f.target > 0 && <i className="tgt" style={{ '--v': `${(f.target / 5) * 100}%` } as CSSProperties} />}
                  {r != null && <i className="ia" style={{ '--v': `${r}%` } as CSSProperties} />}
                </span>
                <span className="v">{n1(f.maturity)}<em>→ {f.target ? n1(f.target) : '—'}</em></span>
              </li>
            );
          })}
        </ul>
        <p className="dx-legend"><span className="cur" />maturidade <span className="tgt" />meta{rd && <><span className="ia" />leitura da IA</>} · de 0 a 5</p>
        {gm && gm.biggest.length > 0 && (
          <div className="dx-sub">
            <p className="dx-k">Maiores gaps</p>
            <ul className="dx-gaps">{gm.biggest.slice(0, 3).map(b => <li key={b.name}><span>{b.name}<em>{b.factor.replace(/^Fatores?\s+/i, '')}</em></span><b>{b.maturity} → {b.target}</b></li>)}</ul>
          </div>
        )}
        {o.priorities.length > 0 && (
          <div className="dx-sub">
            <p className="dx-k">Prioridades da liderança (Board)</p>
            <p className="dx-prio plain">{o.priorities.slice(0, 5).map(x => <span key={x}>{x}</span>)}</p>
          </div>
        )}
      </Block>
      <Block span={5} title="Leitura do diagnóstico" icon={<Ic.Sparkle />} tool="gaps" empty={!rd && !o.priorities.length && 'A leitura do Mapa de Gaps aparece quando o diagnóstico estiver preenchido.'}>
        {rd && <>
          <p className="dx-text">{rd.leitura}</p>
          <ol className="dx-num">{rd.recomendacoes.slice(0, 3).map((x, i) => <li key={i}><span>{i + 1}</span>{x}</li>)}</ol>
        </>}
        {!factors.length && o.priorities.length > 0 && (
          <div className={rd ? 'dx-sub' : ''}>
            <p className="dx-k">Prioridades da liderança (Board)</p>
            <p className="dx-prio plain">{o.priorities.slice(0, 5).map(x => <span key={x}>{x}</span>)}</p>
          </div>
        )}
        {gm && <p className="dx-foot">{gm.owners.pct}% dos indicadores com responsável · {gm.evidence.files} {gm.evidence.files === 1 ? 'arquivo' : 'arquivos'} de evidência</p>}
      </Block>
      <Canvas o={o} />
    </>
  );
}

const CANVAS: Array<[BmcListBlock | 'valueProp', string]> = [
  ['partners', 'par'], ['activities', 'atv'], ['resources', 'rcs'], ['valueProp', 'vp'], ['relationship', 'rel'],
  ['channels', 'can'], ['segments', 'seg'], ['costs', 'cst'], ['revenue', 'rct'],
];
function Canvas({ o }: { o: O }) {
  const b = o.contents.bmc, r = o.readings.bmc;
  const any = !!b && (b.valueProp.trim() || CANVAS.some(([k]) => k !== 'valueProp' && b[k].some(i => i.text.trim())));
  return (
    <Block title="Business Model Canvas" icon={<Ic.Cube />} tool="bmc"
      aside={r ? <Stat v={`${r.consistencia}%`} label="consistência (IA)" /> : null} empty={!any && 'O canvas ainda não foi montado.'}>
      {b && (
        <div className="dx-canvas">
          {CANVAS.map(([k, area]) => {
            if (k === 'valueProp') return <div key={k} className="cv vp" style={{ gridArea: area }}><span className="dx-k">{BMC_LABEL[k]}</span><p>{b.valueProp || '—'}</p></div>;
            const items = b[k].filter(i => i.text.trim());
            return (
              <div key={k} className="cv" style={{ gridArea: area }}>
                <span className="dx-k">{BMC_LABEL[k]}</span>
                <ul>{items.slice(0, 3).map(i => <li key={i.id}>{i.text}</li>)}</ul>
                {items.length > 3 && <em>+{items.length - 3}</em>}
              </div>
            );
          })}
        </div>
      )}
      {r && <p className="dx-foot">{r.resumo}</p>}
    </Block>
  );
}

/* ── 03 · contexto ───────────────────────────────────────────────── */

function Contexto({ o }: { o: O }) {
  const c = o.contents.macro, r = o.readings.macro;
  const m = c ? macroMetrics(c) : null;
  if (!c || !m || !m.total) return <Block title="Macro Cenário" icon={<Ic.Bars />} tool="macro" empty="A matriz do macro cenário ainda não foi preenchida." />;
  const max = Math.max(1, ...MACRO_QUADRANTS.flatMap(([q]) => MACRO_DIMENSIONS.map(([d]) => macroCell(c, q, d).filter(f => f.text.trim()).length)));
  return (
    <>
      <div className="dx-quads s8">
        {m.quadrants.map(q => {
          const meta = MACRO_QUADRANT_META[q.key];
          const line = r?.quadrantes[q.key];
          const facts = c.factors.filter(f => f.quadrant === q.key && f.text.trim());
          return (
            <article key={q.key} className={`dx-b dx-rv dx-quad q-${q.key}`}>
              <header className="dx-b-h"><h3><i className="mc-sym">{meta.symbol}</i>{meta.label}</h3><Stat v={q.count} label={q.count === 1 ? 'fator' : 'fatores'} /></header>
              {line?.fatores ? <p className="dx-text">{line.fatores}</p> : <ul className="dx-list">{facts.slice(0, 3).map(f => <li key={f.id}>{f.text}</li>)}</ul>}
              {line?.impacto && <p className="dx-impact">{line.impacto}</p>}
              {q.dims.length > 0 && <p className="dx-dims">{q.dims.slice(0, 3).map(x => <span key={x.key} className={`mc-chip d-${x.key}`}>{x.label}</span>)}</p>}
            </article>
          );
        })}
      </div>
      <Block span={4} className="dx-matrix" title="A matriz" icon={<Ic.Grid4 />} tool="macro" aside={r ? <Stat v={r.validacao.score} label="validação (IA)" /> : null}>
        <div className="dx-heat" role="img" aria-label="Fatores por quadrante e dimensão">
          <span />
          {MACRO_DIMENSIONS.map(([d]) => <span key={d} className="col" title={MACRO_DIMENSION_META[d].plural}>{MACRO_DIMENSION_META[d].plural.slice(0, 3)}</span>)}
          {MACRO_QUADRANTS.map(([q]) => (
            <Row4 key={q} q={q} counts={MACRO_DIMENSIONS.map(([d]) => macroCell(c, q, d).filter(f => f.text.trim()).length)} max={max} />
          ))}
        </div>
        {r && r.direcionamentos.length > 0 && (
          <div className="dx-sub">
            <p className="dx-k">Direcionamentos</p>
            <ol className="dx-num">{r.direcionamentos.slice(0, 3).map((x, i) => <li key={i}><span>{i + 1}</span>{x}</li>)}</ol>
          </div>
        )}
      </Block>
    </>
  );
}
function Row4({ q, counts, max }: { q: (typeof MACRO_QUADRANTS)[number][0]; counts: number[]; max: number }) {
  return (
    <>
      <span className={`row q-${q}`}><i className="mc-sym">{MACRO_QUADRANT_META[q].symbol}</i></span>
      {counts.map((n, i) => (
        <span key={i} className={`cell q-${q}`} title={`${MACRO_QUADRANT_META[q].label} · ${MACRO_DIMENSION_META[MACRO_DIMENSIONS[i]![0]].plural}: ${n}`}>
          {n > 0 && <i style={{ '--s': `${.42 + .58 * (n / max)}` } as CSSProperties} />}
        </span>
      ))}
    </>
  );
}

/* ── 04 · inovação ───────────────────────────────────────────────── */

function Inovacao({ o }: { o: O }) {
  const c = o.contents.inovacao;
  const m = c ? innovationMetrics(c) : null;
  const batch = o.readings.inovacaoBatch;
  const insights = c?.insights.filter(i => i.text.trim()) ?? [];
  const hz = o.contents.horizontes;
  return (
    <>
      <Block span={7} title="10 Tipos de Inovação" icon={<Ic.Bulb />} tool="inovacao"
        aside={o.readings.inovacao ? <Stat v={`${o.readings.inovacao.alinhamento}%`} label="alinhamento" /> : null}
        empty={!m?.rated && 'O diagnóstico dos 10 tipos ainda não foi preenchido.'}>
        {m && (
          <div className="dx-types">
            {INNOVATION_CATEGORIES.map(cat => (
              <div key={cat.key} className={`dx-cat c-${cat.key}`}>
                <p className="dx-k">{cat.label}</p>
                <ul>
                  {(cat.types as readonly string[]).map(k => {
                    const row = m.rows.find(x => x.key === k)!;
                    return (
                      <li key={k}>
                        <span className="nm">{INNOVATION_META[row.key].label}</span>
                        <span className="dx-scale" title={`Atual ${row.current || '—'} · desejada ${row.target || '—'}`}>
                          <i className="cur" style={{ '--v': `${(row.current / 5) * 100}%` } as CSSProperties} />
                          {row.target > 0 && <i className="tgt" style={{ '--v': `${(row.target / 5) * 100}%` } as CSSProperties} />}
                        </span>
                        <span className={`gap${row.gap >= 3 ? ' hi' : ''}`}>{row.gap > 0 ? `+${row.gap}` : '—'}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Block>
      <Block span={5} title="Prioridades e focos" icon={<Ic.Target />} tool="inovacao" empty={!m?.top.length && !insights.length && 'As prioridades aparecem com o diagnóstico dos 10 tipos.'}>
        {m && m.top.length > 0 && <p className="dx-prio">{m.top.slice(0, 4).map(r => <span key={r.key}>{r.label}<b>+{r.gap}</b></span>)}</p>}
        {batch?.focos.length ? (
          <ul className="dx-focos">{batch.focos.map((f, i) => <li key={i}><b>{f.titulo}</b><span>{f.texto}</span></li>)}</ul>
        ) : o.readings.inovacao ? <p className="dx-text">{o.readings.inovacao.leitura}</p> : null}
        <p className="dx-foot">{insights.length} {insights.length === 1 ? 'insight registrado' : 'insights registrados'}{insights.some(i => i.ai) ? ` · ${insights.filter(i => i.ai).length} da IA` : ''}{batch ? ` · potencial adicional ${batch.potencial.score}/100` : ''}</p>
      </Block>
      <Horizons hz={hz} o={o} />
    </>
  );
}

function Horizons({ hz, o }: { hz?: Horizontes; o: O }) {
  const nav = useNavigate();
  const m = hz ? horizonMetrics(hz) : null;
  const r = o.readings.horizontes;
  if (!hz || !m?.total) return <Block title="3 Horizontes de Inovação" icon={<Ic.Layers />} tool="horizontes" empty="As iniciativas dos três horizontes ainda não foram registradas." />;
  return (
    <Block title="3 Horizontes de Inovação" icon={<Ic.Layers />} tool="horizontes" className="dx-hz"
      aside={r ? <Stat v={`${r.alinhamento}%`} label="alinhamento" /> : null}>
      <div className="dx-hz-g">
        <div className="dx-hz-chart">
          <HorizonChart c={hz} reading={r} busy={false} onPick={() => nav('/strategy/horizontes')} />
          {r && <p className="dx-text sm">{r.leitura}</p>}
        </div>
        <div className="dx-hz-cols">
          {HORIZON_KEYS.map(h => {
            const xs = hz[h].initiatives.filter(i => i.name.trim());
            return (
              <div key={h} className={`h-${h}`}>
                <p className="dx-hz-t"><b>{HORIZON_META[h].code}</b>{HORIZON_META[h].label}<em>{xs.length}</em></p>
                <ul>{xs.map(i => <li key={i.id}><b>{i.name}</b>{i.description && <span>{i.description}</span>}</li>)}</ul>
                {!xs.length && <p className="dx-none">Nenhuma iniciativa.</p>}
              </div>
            );
          })}
        </div>
      </div>
    </Block>
  );
}

/* ── 05 · execução ───────────────────────────────────────────────── */

function Execucao({ o }: { o: O }) {
  const nav = useNavigate();
  const c = o.contents.w5h2;
  const m = c ? w5h2Metrics(c) : null;
  const acts = c ? sortActions(c.actions.filter(a => a.what.trim())) : [];
  const today = localToday();
  if (!c || !m?.total) return <Block title="Plano de Ação" icon={<Ic.CheckSquare />} tool="w5h2" empty="O plano de ação ainda não tem ações." />;
  return (
    <>
      <div className="dx-kpis s12 dx-rv">
        <div><span>Ações{m.done ? ` · ${m.done} ${m.done === 1 ? 'concluída' : 'concluídas'}` : ''}</span><b>{m.total}</b><p>{PRIORIDADES.map(p => <em key={p} className={`pa-chip p-${p}`}>{PRIORITY_LABEL[p]} {m.by[p]}</em>)}</p></div>
        <div><span>Investimento previsto</span><b>{m.priced ? brl(m.invest) : '—'}</b><p>{m.priced ? `${m.priced} de ${m.total} com valor` : 'Nenhuma ação com valor ainda'}</p></div>
        <div><span>Próxima entrega</span><b>{m.next ? dateBR(m.next) : '—'}</b><p>{m.next ? acts.find(a => a.when === m.next)?.what : 'Sem datas ainda'}</p></div>
        <div className={m.open ? 'warn' : ''}><span>Sem responsável ou prazo</span><b>{m.open}</b><p>{m.open ? 'Complete no Plano de Ação' : 'Tudo com dono e data'}</p></div>
      </div>
      <Block title="Linha do tempo" icon={<Ic.Clock />} tool="w5h2">
        <Timeline acts={acts} />
      </Block>
      <Block title="As ações, por prioridade" icon={<Ic.CheckSquare />} tool="w5h2"
        aside={<button type="button" className="dx-more" onClick={() => nav('/strategy/w5h2')}>Abrir o plano<Ic.ArrowRight /></button>}>
        <ul className="dx-acts">
          {acts.map(a => (
            <li key={a.id} className={a.doneAt ? 'done' : isOverdue(a, today) ? 'late' : undefined}>
              <span className={`pa-chip p-${a.priority}`}>{PRIORITY_LABEL[a.priority]}</span>
              <span className="w">{a.doneAt && <Ic.Check className="ok" />}{a.what}{a.doneAt ? <em>Concluída em {dateBR(a.doneAt)}</em> : a.why && <em>{a.why}</em>}</span>
              <span className="who">{a.who || <i>sem responsável</i>}</span>
              <span className="when">{a.when ? dateBR(a.when) : <i>sem prazo</i>}</span>
              <span className="much">{a.howMuch != null ? brl(a.howMuch) : '—'}</span>
            </li>
          ))}
        </ul>
      </Block>
    </>
  );
}

const MONTH = new Intl.DateTimeFormat('pt-BR', { month: 'short' });
function Timeline({ acts }: { acts: W5h2Action[] }) {
  const [ref, W] = useWidth<HTMLDivElement>();
  const dated = acts.filter(a => a.when);
  const today = localToday();
  if (!dated.length) return <p className="gd-empty">As ações ainda não têm prazo — a linha do tempo aparece com as datas.</p>;
  const first = [today, ...dated.map(a => a.when)].sort()[0]!;
  const last = dated.map(a => a.when).sort().at(-1)!;
  const start = new Date(`${first.slice(0, 7)}-01T00:00:00`);
  const endD = new Date(`${last.slice(0, 7)}-01T00:00:00`);
  endD.setMonth(endD.getMonth() + 1);
  const span = endD.getTime() - start.getTime();
  const x = (iso: string) => ((new Date(`${iso}T12:00:00`).getTime() - start.getTime()) / span) * 100;
  const months: Array<{ at: number; label: string; year: string | null }> = [];
  for (const d = new Date(start); d < endD; d.setMonth(d.getMonth() + 1)) {
    months.push({ at: ((d.getTime() - start.getTime()) / span) * 100, label: MONTH.format(d).replace('.', ''), year: d.getMonth() === 0 || !months.length ? String(d.getFullYear()).slice(2) : null });
  }
  /* os nomes em duas faixas (acima e abaixo do ponto), sem encavalar; o que não couber aparece ao passar o mouse */
  const place = (lane: W5h2Action[]) => {
    const rows: Array<Array<[number, number]>> = [[], []];
    return [...lane].sort((a, b) => a.when.localeCompare(b.when)).map(a => {
      const px = (x(a.when) / 100) * W;
      const w = Math.min(200, a.what.length * 6.2 + 8);
      const end = px + 10 + w > W;
      const span: [number, number] = end ? [px - 10 - w, px - 10] : [px + 10, px + 10 + w];
      const row = W ? rows.findIndex(r => r.every(([s0, s1]) => span[1] + 10 < s0 || span[0] > s1 + 10)) : 0;
      if (row >= 0) rows[row]!.push(span);
      return { a, at: x(a.when), end, row, w };
    });
  };
  return (
    <div className="dx-tl">
      <div className="dx-tl-axis" ref={ref}>
        {months.map(mo => <span key={mo.at} style={{ left: `${mo.at}%` }}>{mo.label}{mo.year && <em>{mo.year}</em>}</span>)}
      </div>
      {PRIORIDADES.map(p => (
        <div key={p} className={`dx-lane p-${p}`}>
          <span className="dx-lane-l">{PRIORITY_LABEL[p]}</span>
          <div className="dx-lane-t">
            {months.map(mo => <i key={mo.at} className="grid" style={{ left: `${mo.at}%` }} />)}
            {today >= first && <i className="now" style={{ left: `${x(today)}%` }} />}
            {place(dated.filter(a => a.priority === p)).map(({ a, at, end, row, w }) => (
              <span key={a.id} className={`pin${row === 1 ? ' alt' : ''}${row < 0 ? ' hid' : ''}${end ? ' end' : ''}${a.doneAt ? ' done' : isOverdue(a, today) ? ' late' : ''}`}
                style={{ left: `${at}%`, '--w': `${w}px` } as CSSProperties}
                title={`${a.what} · ${dateBR(a.when)}${a.doneAt ? ` · concluída em ${dateBR(a.doneAt)}` : isOverdue(a, today) ? ' · prazo vencido' : ''}`}>
                <i /><b>{a.what}</b>
              </span>
            ))}
          </div>
        </div>
      ))}
      <div className="dx-tl-now">{today >= first && <span style={{ left: `${x(today)}%` }}>Hoje · {dateBR(today)}</span>}</div>
      {acts.length > dated.length && <p className="dx-foot">{acts.length - dated.length} {acts.length - dated.length === 1 ? 'ação ainda sem prazo' : 'ações ainda sem prazo'}.</p>}
    </div>
  );
}

/* ── o DNA no Studio ──────────────────────────────────────────────── */

function Studio({ o }: { o: O }) {
  const nav = useNavigate();
  const src = Object.entries(DNA_SOURCES) as Array<[string, { label: string; tool: StrategyToolKey }]>;
  return (
    <section className="dx-studio dx-rv" aria-labelledby="dx-studio-t">
      <div className="dx-studio-h">
        <p className="dx-k">O DNA no Studio</p>
        <h2 id="dx-studio-t">Cada entrega do Studio parte daqui.</h2>
        <p>O Studio lê esta versão do DNA — com os materiais, as entrevistas do Board e as evidências — para escrever na voz e na estratégia da organização.</p>
        <p className="dx-srcs"><Stat v={o.sources.materials} label="materiais lidos" /><Stat v={o.sources.interviews} label="entrevistas do Board" /><Stat v={o.sources.evidence} label="evidências" /></p>
        <button type="button" className="btn-primary" onClick={() => nav('/studio')}>Abrir o Studio<Ic.ArrowRight /></button>
      </div>
      <ul className="dx-cover" aria-label="Cobertura das fontes do DNA">
        {src.map(([k, s]) => {
          const v = Math.round((o.live.coverage[k] ?? 0) * 100);
          return <li key={k} className={v >= 100 ? 'full' : v === 0 ? 'none' : ''}><span>{s.label}</span><Meter v={v} /><b>{v}%</b></li>;
        })}
      </ul>
    </section>
  );
}
